import { format } from "date-fns";

export const DATE_FORMAT = "MM/dd/yyyy, h:mm a";

export function formatDateTime(date: Date | string): string {
  return format(new Date(date), DATE_FORMAT);
}

/**
 * Extracts and forwards authorization headers from a request
 */
export function forwardAuthHeaders(request: Request): Record<string, string> {
  const authHeaders = ["Authorization", "X-Pebble-Authorization"];

  return Object.fromEntries(
    authHeaders
      .map((header) => [header, request.headers.get(header)])
      .filter(([, value]) => value)
  );
}

const HEADERS_TO_FORWARD = ["trace_id"];

/**
 * Builds a Response object with headers forwarded from the backend response
 * @param backendResponse - The response from the backend
 * @param data - The response body data
 * @returns A new Response with forwarded headers
 */
export function buildResponseWithHeaders(
  backendResponse: Response,
  data: string
): Response {
  const responseHeaders: Record<string, string> = {
    "Content-Type": "application/json",
  };

  for (const headerName of HEADERS_TO_FORWARD) {
    const headerValue = backendResponse.headers.get(headerName);
    if (headerValue) {
      responseHeaders[headerName] = headerValue;
    }
  }

  return new Response(data, {
    status: backendResponse.status,
    statusText: backendResponse.statusText,
    headers: responseHeaders,
  });
}

/**
 * Groups security explanations by the symbol only (for portfolio-explain)
 */
export function groupSecurityExplanationsBySymbol(
  content: string | string[]
): Record<string, string[]> {
  const items = Array.isArray(content) ? content : [content];
  const grouped: Record<string, string[]> = {};

  items.forEach((item) => {
    // Extract first part from parentheses (e.g., "Some text (AAPL)" -> "AAPL")
    const match = item.match(/\(([^\s)]+)/);
    const symbol = match ? match[1] : "Other";

    if (!grouped[symbol]) {
      grouped[symbol] = [];
    }

    grouped[symbol].push(item);
  });

  return grouped;
}

/**
 * Groups security explanations by the full company name in parentheses (for fund-explain)
 */
export function groupSecurityExplanationsByCompany(
  content: string | string[]
): Record<string, string[]> {
  const items = Array.isArray(content) ? content : [content];
  const grouped: Record<string, string[]> = {};

  items.forEach((item) => {
    // Extract full content from parentheses (e.g., "(NVDA Nvidia Corp)" -> "NVDA Nvidia Corp")
    const match = item.match(/\(([^)]+)\)/);
    const companyName = match ? match[1] : "Other";

    if (!grouped[companyName]) {
      grouped[companyName] = [];
    }

    grouped[companyName].push(item);
  });

  return grouped;
}

/**
 * Groups security explanations - auto-detects format
 */
export function groupSecurityExplanations(
  content: string | string[]
): Record<string, string[]> {
  const items = Array.isArray(content) ? content : [content];

  // Check first item to determine format
  if (items.length > 0) {
    const firstItem = items[0];
    // Check if parentheses contain a space (indicating full company name)
    const match = firstItem.match(/\(([^)]+)\)/);
    if (match && match[1].includes(" ")) {
      // Full company name format (fund-explain)
      return groupSecurityExplanationsByCompany(content);
    }
  }

  // Default to symbol-only format (portfolio-explain)
  return groupSecurityExplanationsBySymbol(content);
}

/**
 * Groups sector explanations by the sector name (first word followed by " -")
 */
export function groupSectorExplanations(
  content: string | string[]
): Record<string, string[]> {
  const items = Array.isArray(content) ? content : [content];
  const grouped: Record<string, string[]> = {};

  items.forEach((item) => {
    // Extract sector name from pattern "SectorName - rest of text"
    const match = item.match(/^([A-Za-z]+)\s*-\s*/);
    const sectorName = match ? match[1] : "Other";

    if (!grouped[sectorName]) {
      grouped[sectorName] = [];
    }

    grouped[sectorName].push(item);
  });

  return grouped;
}
